import React, { useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Typography, Drawer } from 'antd';
import {
  DashboardOutlined,
  ShoppingOutlined,
  TagsOutlined,
  ShoppingCartOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  GlobalOutlined,
  FileTextOutlined,
  MailOutlined,
  PlayCircleOutlined,
  GiftOutlined,
  NotificationOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '../../Utils/api';
import styled from 'styled-components';
import { useLanguage } from '../../Context/LanguageContext';
import LanguageSelector from '../Components/LanguageSelector';
import { useIsMobile } from '../../Utils/useIsMobile';

const { Header, Sider, Content } = Layout;
const { Title } = Typography;

const StyledLayout = styled(Layout)`
  min-height: 100vh;
`;

const StyledHeader = styled(Header)`
  background: #fff;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  z-index: 10;

  @media (max-width: 767.98px) {
    padding: 0 12px;
  }
`;

const StyledContent = styled(Content)`
  margin: 24px;
  padding: 24px;
  background: #fff;
  border-radius: 8px;
  min-height: 280px;

  @media (max-width: 767.98px) {
    margin: 8px;
    padding: 12px;
  }
`;

const Logo = styled.div`
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 18px;
  color: #1890ff;
  border-bottom: 1px solid #f0f0f0;
`;

const AdminLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLanguage, setCurrentLanguage } = useLanguage();

  const handleLogout = () => {
    authAPI.logout();
    navigate('/login');
  };

  const menuItems = [
    { key: '/dashboard', icon: <DashboardOutlined />, label: 'Dashboard' },
    { key: '/products', icon: <ShoppingOutlined />, label: 'Produkte' },
    { key: '/categories', icon: <TagsOutlined />, label: 'Kategorien' },
    { key: '/orders', icon: <ShoppingCartOutlined />, label: 'Verkäufe' },
    { key: '/sell-requests', icon: <MailOutlined />, label: 'Verkaufsanfragen' },
    { key: '/search-requests', icon: <MailOutlined />, label: 'Suchanfragen' },
    { key: '/discount-codes', icon: <TagsOutlined />, label: 'Rabattcodes' },
    { key: '/withdrawals', icon: <MailOutlined />, label: 'Widerrufe' },
    { key: '/contacts', icon: <MailOutlined />, label: 'Kontaktanfragen' },
    { key: '/giveaways', icon: <GiftOutlined />, label: 'Gewinnspiele' },
    { key: '/newsletter', icon: <NotificationOutlined />, label: 'Newsletter' },
    { key: '/hero-slider', icon: <PlayCircleOutlined />, label: 'Home-Slider' },
    { key: '/pages', icon: <FileTextOutlined />, label: 'Seitenverwaltung' },
    { key: '/languages', icon: <GlobalOutlined />, label: 'Sprachen' },
    { key: '/settings', icon: <SettingOutlined />, label: 'Einstellungen' },
  ];

  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Ausloggen',
      onClick: handleLogout,
    },
  ];

  const navigation = (
    <Menu
      mode="inline"
      selectedKeys={[location.pathname]}
      items={menuItems}
      onClick={({ key }) => { navigate(key); setDrawerOpen(false); }}
      style={{ borderRight: 0 }}
    />
  );

  return (
    <StyledLayout>
      {isMobile ? (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title="MR Luxury Watches"
          size={280}
          styles={{ body: { padding: 0 } }}
        >
          {navigation}
        </Drawer>
      ) : (
        <Sider trigger={null} collapsible collapsed={collapsed} theme="light">
          <Logo>{collapsed ? 'MR' : 'MR Luxury Watches'}</Logo>
          {navigation}
        </Sider>
      )}
      <Layout>
        {isMobile ? (
          <StyledHeader>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
              <MenuOutlined
                style={{ fontSize: 20, padding: 12, margin: -12, cursor: 'pointer' }}
                onClick={() => setDrawerOpen(true)}
                data-testid="admin-mobile-menu-btn"
              />
              <span style={{ fontWeight: 'bold', fontSize: 16, color: '#1890ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                MR Luxury Watches
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <LanguageSelector value={currentLanguage} onChange={setCurrentLanguage} compact />
              <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']}>
                <Avatar icon={<UserOutlined />} style={{ cursor: 'pointer', flexShrink: 0 }} />
              </Dropdown>
            </div>
          </StyledHeader>
        ) : (
        <StyledHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
              style: { fontSize: '18px', cursor: 'pointer' },
              onClick: () => setCollapsed(!collapsed),
            })}
            <Title level={4} style={{ margin: 0 }}>
              Admin-Bereich
            </Title>
            
            {/* Global Language Selector */}
            <div style={{ marginLeft: 'auto', marginRight: 16 }}>
              <LanguageSelector
                value={currentLanguage}
                onChange={setCurrentLanguage}
              />
            </div>
          </div>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Avatar icon={<UserOutlined />} style={{ cursor: 'pointer' }} />
          </Dropdown>
        </StyledHeader>
        )}
        <StyledContent>
          <Outlet />
        </StyledContent>
      </Layout>
    </StyledLayout>
  );
};

export default AdminLayout;
